import {
  Component,
  OnInit,
  OnChanges,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { Customer } from '../customer-table/customer-table.component';
import { CustomerService } from '../../services/customer.service';
import { usaPhoneValidator } from '../../shared/validators/phone.validator';
import { strictEmailValidator } from '../../shared/validators/email.validator';
import { standardNameValidator } from '../../shared/validators/name.validator';

/**
 * CustomerInfoTabComponent manages an editable customer form used to view and update a customer's basic contact and type information.
 * It handles form initialization, change detection, validation, saving updates to the backend, and user feedback via messages.
 * Key behaviors include populating the form from different customer data shapes (first_name/last_name or legacy name), tracking unsaved changes, and formatting payloads for the update API.
 * @component
 * @selector app-customer-info-tab
 * @example
 *   <app-customer-info-tab [customer]="selectedCustomer" (customerUpdated)="onCustomerUpdated()"></app-customer-info-tab>
 * @input {Customer | null} customer - Customer object used to populate the form. May be null; supports both modern fields (first_name/last_name) and legacy name parsing.
 * @output {EventEmitter<void>} customerUpdated - Emitted after a successful save to notify parent components that the customer's data was updated.
 *
 * Lifecycle hooks:
 * - ngOnInit(): Initializes the reactive form and attaches value change listeners.
 * - ngOnChanges(): Re-populates the form when the @input customer changes.
 *
 * Key methods:
 * - initForm(): Builds the FormGroup with validators, subscribes to valueChanges, and populates the form when a customer exists.
 * - populateForm(): Maps the incoming customer data into the form fields, supports backward compatibility with a single name field, and stores an original snapshot for change detection/reset.
 * - onSave(): Validates (format-only where fields exist), constructs API payload, calls CustomerService.updateCustomer, displays success/error messages, updates the original snapshot, and emits customerUpdated on success.
 * - onCancel(): Restores the form to the original snapshot, clears validation state, and marks the form pristine/untouched.
 * - checkFormChanges(): Compares current form values to the original snapshot to set hasFormChanged.
 * - markFormGroupTouched(): Marks all controls as touched to trigger validation UI.
 * - isFieldInvalid(fieldName: string): Returns true if the specified field is invalid and has been interacted with.
 * - getFieldError(fieldName: string): Returns a user-friendly error message for a touched field based on configured validators.
 * - onPhoneKeyup(): Ensures phone control is marked touched and re-validated on input events.
 *
 * Notes:
 * - All form fields are optional; validators only enforce format when values are present.
 * - The component uses NotificationService for user feedback and CustomerService to persist updates.
 */
@Component({
  selector: 'app-customer-info-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatInputModule, MatButtonModule],
  templateUrl: './customer-info-tab.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./customer-info-tab.component.scss'],
})
export class CustomerInfoTabComponent implements OnInit, OnChanges {
  @Input() customer: Customer | null = null;
  @Output() customerUpdated = new EventEmitter<void>();

  customerForm!: FormGroup;
  originalCustomerData: any = null;
  hasFormChanged = false;
  isSaving = false;

  constructor(
    private fb: FormBuilder,
    private customerService: CustomerService,
    private messageService: NotificationService
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  ngOnChanges(): void {
    if (!this.customerForm || !this.customer) return;
    if (!this.hasFormChanged) {
      this.populateForm();
      return;
    }
    // A refreshed copy of the same customer (CWS-1925) must not wipe what staff are typing, but
    // Cancel and the change check compare against the saved values, so those move on.
    this.originalCustomerData = this.formDataOf(this.customer);
    this.checkFormChanges();
  }

  /**
   * Initialize the component's reactive customer form with controls, validators, and a change listener.
   * @example
   * this.initForm()
   * undefined
   * @returns {{void}} No return value — sets up this.customerForm with firstName, lastName, phone, and email controls (with their validators), subscribes to valueChanges to call checkFormChanges, and calls populateForm if this.customer exists.
   **/
  private initForm(): void {
    this.customerForm = this.fb.group({
      firstName: ['', [standardNameValidator()]],
      lastName: ['', [standardNameValidator()]],
      phone: ['', [usaPhoneValidator()]],
      email: ['', [strictEmailValidator()]],
    });

    // Listen for form changes
    this.customerForm.valueChanges.subscribe(() => {
      this.checkFormChanges();
    });

    if (this.customer) {
      this.populateForm();
    }
  }

  /**
   * Populate the customer form fields from the component's customer object, parsing name when needed, then patch the form and store original data while resetting change state.
   * @example
   * this.populateForm()
   * void
   * @returns {void} Populates the customerForm FormGroup, sets originalCustomerData, and sets hasFormChanged to false.
   */
  private populateForm(): void {
    if (!this.customer) return;

    const formData = this.formDataOf(this.customer);

    this.customerForm.patchValue(formData);

    // Store original data for comparison and reset functionality
    this.originalCustomerData = { ...formData };
    this.hasFormChanged = false;
  }

  /** The form values for a customer: the actual first_name/last_name when present, else the parsed name. */
  private formDataOf(customer: NonNullable<CustomerInfoTabComponent['customer']>) {
    // Use the actual first_name and last_name fields if available, fallback to parsing name
    let firstName = '';
    let lastName = '';

    if (customer.first_name) {
      firstName = customer.first_name;
      lastName = customer.last_name || '';
    } else if (customer.name) {
      // Fallback to parsing name for backward compatibility
      const nameParts = customer.name.split(' ');
      firstName = nameParts[0] || '';
      lastName = nameParts.slice(1).join(' ') || '';
    }

    return {
      firstName: firstName,
      lastName: lastName,
      phone: customer.contact || '',
      email: customer.email || '',
    };
  }

  /**
   * Save customer form changes by validating the form, preparing an API payload, and updating the customer via customerService; shows success/error messages and emits an update event.
   * @example
   * nSave()
   * undefined
   * @returns {{void}} Performs an asynchronous update with side effects (message notifications, state updates, and emits customerUpdated) and does not return a value.
   **/
  onSave(): void {
    // Form validation check removed - all fields are now optional
    // Only validate format if fields have values
    if (!this.customerForm.valid) {
      this.markFormGroupTouched();
      return;
    }

    if (!this.customer?.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Customer ID is required to save changes',
      });
      return;
    }

    this.isSaving = true;
    const formValue = this.customerForm.value;

    // Prepare payload matching the API format
    const payload = {
      first_name: formValue.firstName,
      last_name: formValue.lastName || null,
      email: formValue.email,
      contact: formValue.phone,
      license_plate: this.customer.license_plate || '',
      customer_status: this.customer.customer_status || 'active',
    };

    this.customerService.updateCustomer(this.customer.id, payload).subscribe({
      next: response => {
        this.messageService.add({
          severity: 'success',
          summary: 'Customer records updated!',
          detail: `${`${formValue.firstName} ${formValue.lastName}`.trim()} as been updated successfully.`,
        });

        // Update original data to reflect saved state
        this.originalCustomerData = { ...formValue };
        this.hasFormChanged = false;
        this.isSaving = false;

        // Emit event to notify parent component that customer was updated
        this.customerUpdated.emit();
      },
      error: error => {
        console.error('Error updating customer:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || 'Failed to update customer information',
        });
        this.isSaving = false;
      },
    });
  }

  /**
   * Reverts the customer form to the original data and resets validation and touched/pristine state.
   * @example
   * nCancel()
   * undefined
   * @returns {void} Restores originalCustomerData into the form, clears validation errors, and marks the form untouched and pristine.
   **/
  onCancel(): void {
    if (this.originalCustomerData) {
      this.customerForm.patchValue(this.originalCustomerData);
      this.hasFormChanged = false;

      // Reset form validation state
      this.customerForm.markAsUntouched();
      this.customerForm.markAsPristine();
      Object.keys(this.customerForm.controls).forEach(key => {
        this.customerForm.get(key)?.setErrors(null);
      });
    }
  }

  private checkFormChanges(): void {
    if (!this.originalCustomerData) {
      this.hasFormChanged = false;
      return;
    }

    const currentValues = this.customerForm.value;
    this.hasFormChanged =
      JSON.stringify(currentValues) !== JSON.stringify(this.originalCustomerData);
  }

  private markFormGroupTouched(): void {
    Object.keys(this.customerForm.controls).forEach(key => {
      const control = this.customerForm.get(key);
      if (control) {
        control.markAsTouched();
      }
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.customerForm.get(fieldName);
    return Boolean(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.customerForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) return `${this.getFieldDisplayName(fieldName)} is required`;
      if (field.errors['email']) return 'Invalid email address.';
      if (field.errors['usaPhone']) return field.errors['usaPhone'].message;
      if (field.errors['standardName']) return field.errors['standardName'].message;
      if (field.errors['strictEmail']) return field.errors['strictEmail'].message;
    }
    return '';
  }

  private getFieldDisplayName(fieldName: string): string {
    const displayNames: Record<string, string> = {
      firstName: 'First Name',
      lastName: 'Last Name',
      phone: 'Phone',
      email: 'Email',
    };
    return displayNames[fieldName] || fieldName;
  }

  onPhoneKeyup(): void {
    const phoneControl = this.customerForm.get('phone');
    if (phoneControl) {
      phoneControl.markAsTouched();
      phoneControl.updateValueAndValidity();
    }
  }
}
